{
  "title": "Privacy Policy “Oversight”",
  "sections": [
    {
      "id": "introduction",
      "title": "1. Introduction and purpose of data processing",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "The internal HR tool \"Oversight\" forms the strategic infrastructure for managing our human resources. In accordance with our data protection accountability obligations and to maintain a transparent relationship of trust between employer and employee, we hereby inform you about the processing of personal data within this system. The processing is strictly purpose-bound in accordance with Art. 6 para. 1 lit. b) GDPR (fulfillment of the employment relationship) and for the purposes of the legitimate interests pursued by the controller. \"Oversight\" serves exclusively:"
            }
          ]
        },
        {
          "type": "bullets",
          "items": [
            { "text": "the central administration of employee master data," },
            { "text": "efficient absence management (vacation, illness)," },
            {
              "text": "as well as the administration of the individual training budget and the associated employee development. From a data protection perspective, this processing is essential for operational efficiency and the fulfillment of the employer's duty of care – such as the correct documentation of rest periods or the promotion of professional qualifications. The following list details the data categories collected for this purpose."
            }
          ]
        }
      ]
    },
    {
      "id": "categories",
      "title": "2. Categories of processed personal data",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "In accordance with the principle of data minimization (Art. 5 para. 1 lit. c) GDPR), only data that is absolutely necessary for HR administration is processed in \"Oversight\":"
            }
          ]
        },
        {
          "type": "bullets",
          "items": [
            {
              "label": "Master data:",
              "text": "First and last name, business email address, employee photo, and internal standard identification numbers (IDs)."
            },
            {
              "label": "Absence management:",
              "text": "Documentation of vacation time and sick leave, including descriptions of the reason for absence provided by the employee."
            },
            {
              "label": "Staff development (education budget):",
              "text": "Details on training measures (category, date, title, description, budget amount, course links/books, justifications and certificates of completion)."
            },
            {
              "label": "Hardware resources:",
              "text": "Documentation of hardware requests, including descriptions of their respective purposes."
            }
          ]
        }
      ]
    },
    {
      "id": "data-transmission",
      "title": "3. Data transmission and system integrations",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "Oversight is integrated into the company's technical infrastructure to ensure data consistency and eliminate the risk of administrative silos. These integrations transform the tool into a synchronized hub for real-time internal operations."
            }
          ]
        },
        {
          "type": "paragraph",
          "runs": [
            { "text": "The following active integrations are mandated for system functionality:" }
          ]
        },
        {
          "type": "bullets",
          "items": [
            {
              "label": "Google Calendar:",
              "text": "For the automated synchronization and management of employee absences, ensuring organizational visibility."
            },
            {
              "label": "Jira (Tempo):",
              "text": "For the precise logging of hours related to vacations and absences, supporting accurate resource management."
            },
            {
              "label": "Firebase / Google Cloud Platform (GCP):",
              "text": "Serving as the foundational database and architectural environment for all data processing activities."
            }
          ]
        }
      ]
    },
    {
      "id": "hosting",
      "title": "4. Hosting and transfer to third countries",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "The selection of hosting providers is governed by the strategic requirement for high availability and strict adherence to European data protection standards."
            }
          ]
        },
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "Oversight is cloud-hosted via Google Cloud Platform (GCP), utilizing the Firebase database interface. All data resides strictly on servers located within the EU/EEA. Furthermore, all subprocessors are localized within the EU/EEA, and no third-country transfers (e.g., to the United States) are performed. This localized hosting strategy ensures that all data remains within the jurisdiction of European data protection authorities, providing a secure perimeter for the implementation of technical safeguards."
            }
          ]
        }
      ]
    },
    {
      "id": "toms",
      "title": "5. Technical and organizational measures (TOMs)",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "In accordance with Article 32 GDPR, comprehensive measures have been implemented to ensure the confidentiality, integrity and availability of the data:"
            }
          ]
        },
        {
          "type": "bullets",
          "items": [
            {
              "label": "Access control:",
              "text": "A restrictive, role-based access control (RBAC) concept ensures that access is limited to the \"need-to-know\" principle."
            },
            {
              "label": "Encryption:",
              "text": "By using Firebase technologies, data is encrypted both at rest and during transmission according to the current state of the art."
            },
            {
              "label": "Authentication:",
              "text": "System access is protected by two-factor authentication (2FA). This is directly linked to the company's identity provider (IdP) (Google SSO), which complies with current security standards (best practices) in SaaS architectures. This architecture effectively minimizes the risk of data breaches and unauthorized access."
            }
          ]
        }
      ]
    },
    {
      "id": "storage-duration",
      "title": "6. Storage duration and deletion concept",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "We are committed to the principle of data limitation (Art. 5 para. 1 lit. e) GDPR), but would like to point out the current technical limitations of the tool. Currently, no automated deletion routines are implemented in \"Oversight\". Upon termination of employment, profiles are initially "
            },
            { "text": "archived", "bold": true },
            {
              "text": ". Since this is only a temporary solution under the law, compliance with the final deletion periods is currently tied to a "
            },
            { "text": "manual process.", "bold": true },
            {
              "text": " To meet legal requirements, deletion requests after an employee leaves the company or after the expiry of statutory retention periods must be manually initiated by the administration. We consider the current archiving function a temporary technical status and are working on optimizing system-side deletion cycles."
            }
          ]
        }
      ]
    },
    {
      "id": "data-subject-rights",
      "title": "7. Data subject rights and contact",
      "blocks": [
        {
          "type": "paragraph",
          "runs": [
            {
              "text": "Every employee has the right to information, rectification, erasure, and restriction of processing, as well as the right to data portability. Due to the current system architecture, independent editing or direct data export by the user (self-service) is not possible. However, this does not restrict your rights."
            }
          ]
        },
        {
          "type": "bullets",
          "items": [
            {
              "label": "Information and portability:",
              "text": "Upon request, we will provide your data in a structured, common format (manual export)."
            },
            {
              "label": "Correction and deletion:",
              "text": "Corrections or deletions will be carried out by the administration after verification of the statutory deadlines. Please direct all requests to assert your rights to your assigned Unit Lead. They will coordinate the necessary manual steps to fulfill your request in accordance with the statutory deadlines."
            }
          ]
        }
      ]
    }
  ]
}
